import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Group, Ungroup, ArrowLeft, Monitor, Tablet, Smartphone, Save, Rocket, Undo2, X, Plus, Magnet, Move, Type, Heading, Image as ImageIcon, MousePointerClick, ExternalLink, LayoutList, Trash2, PanelTop, FilePlus2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ElementPanel } from "@/components/editor/ElementPanel";
import { CustomPagesDialog } from "@/components/editor/CustomPagesDialog";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const ORIGIN = window.location.origin;
const BASE_PAGES = [["/", "Startseite"], ["/fahrzeuge", "Fahrzeuge"], ["/aktionen", "Aktionen"], ["/termin", "Termin buchen"],
  ["/bewertungen", "Bewertungen"], ["/kontakt", "Kontakt"], ["/reifenlager", "Reifenlager"], ["/login", "Kunden-Login"],
  ["/registrieren", "Registrieren"], ["/impressum", "Impressum"], ["/datenschutz", "Datenschutz"], ["/agb", "AGB"]];
const DEVICES = [["desktop", Monitor, "100%"], ["tablet", Tablet, "820px"], ["mobile", Smartphone, "400px"]];
const ADD = [["heading", Heading, "Überschrift"], ["text", Type, "Text"], ["image", ImageIcon, "Bild"], ["button", MousePointerClick, "Knopf"], ["tabs", PanelTop, "Reiter (Tabs)"]];
const DEFAULT_TABS = JSON.stringify([{ t: "Öffnungszeiten", c: "Mo - Fr: 8:00 - 18:00 Uhr" }, { t: "Anfahrt", c: "Adresse hier eintragen" }]);
const clean = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== ""));
const zOf = (ov = {}) => ov.z ?? (ov.dx || ov.dy ? 5 : 0);
const groupPaths = (elements, path) => {
  const g = elements[path]?.g;
  return g ? Object.keys(elements).filter((k) => elements[k].g === g) : [path];
};
const PER_ELEMENT = ["text", "src", "href", "g"];
const nextZ = (elements, paths, act) => {
  const cur = Math.max(...paths.map((k) => zOf(elements[k])));
  const others = Object.entries(elements).filter(([k]) => !paths.includes(k)).map(([, o]) => zOf(o));
  if (act === "front") return Math.max(0, ...others) + 1;
  if (act === "back") return Math.min(0, ...others) - 1;
  if (act === "up") { const above = others.filter((z) => z >= cur); return above.length ? Math.min(...above) + 1 : cur + 1; }
  const below = others.filter((z) => z <= cur);
  return below.length ? Math.max(...below) - 1 : cur - 1;
};

export default function VisualEditorPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem("adminToken");
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const frame = useRef(null);
  const [pages, setPages] = useState(null);
  const [saved, setSaved] = useState("");
  const [published, setPublished] = useState("");
  const [history, setHistory] = useState([]);
  const [customPages, setCustomPages] = useState([]);
  const [pagesDialog, setPagesDialog] = useState(false);
  const PAGES = [...BASE_PAGES, ...customPages.map((p) => [`/seite/${p.slug}`, p.title])];
  const [tabs, setTabs] = useState(BASE_PAGES.map((p) => p[0]));
  const [active, setActive] = useState("/");
  const [mode, setMode] = useState("snap");
  const [device, setDevice] = useState("desktop");
  const [selected, setSelected] = useState(null);
  const [panel, setPanel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [multi, setMulti] = useState([]);

  useEffect(() => {
    if (!token) { navigate("/admin"); return; }
    axios.get(`${API}/admin/page-overrides`, auth).then((r) => {
      setPages(r.data.draft || {}); setSaved(JSON.stringify(r.data.draft || {})); setPublished(JSON.stringify(r.data.published || {}));
    }).catch((e) => { if (e.response?.status === 401) { localStorage.removeItem("adminToken"); navigate("/admin"); } else toast.error("Editor konnte nicht geladen werden"); });
  }, []);

  const loadCustomPages = useCallback(() => axios.get(`${API}/pages`).then((r) => { setCustomPages(r.data); return r.data; }), []);
  useEffect(() => {
    loadCustomPages().then((list) => setTabs((t) => [...t, ...list.map((p) => `/seite/${p.slug}`).filter((x) => !t.includes(x))]));
  }, [loadCustomPages]);

  const post = useCallback((msg) => frame.current?.contentWindow?.postMessage(msg, ORIGIN), []);
  useEffect(() => { if (pages) post({ type: "at-ve-data", pages }); }, [pages, post]);
  useEffect(() => { post({ type: "at-ve-mode", mode }); }, [mode, post]);

  const page = pages?.[active] || { elements: {}, added: [] };
  const update = useCallback((fn) => {
    setPages((prev) => {
      setHistory((h) => [...h.slice(-79), prev]);
      const cur = prev[active] || { elements: {}, added: [] };
      return { ...prev, [active]: fn({ elements: { ...cur.elements }, added: [...(cur.added || [])] }) };
    });
  }, [active]);

  const patchEl = useCallback((path, patch) => update((p) => {
    const base = p.elements[path] || {};
    for (const m of groupPaths(p.elements, path)) {
      const cur = p.elements[m] || {};
      const next = { ...cur };
      for (const [k, v] of Object.entries(patch)) {
        if (m === path) next[k] = v;
        else if (k === "dx" || k === "dy") next[k] = (cur[k] || 0) + ((v || 0) - (base[k] || 0)) || undefined;
        else if (!PER_ELEMENT.includes(k)) next[k] = v;
      }
      const c = clean(next);
      if (Object.keys(c).length) p.elements[m] = c; else delete p.elements[m];
    }
    return p;
  }), [update]);

  const removeEl = useCallback((path) => {
    update((p) => {
      const ms = groupPaths(p.elements, path);
      const addedIds = ms.filter((m) => m.startsWith("a:")).map((m) => m.slice(2));
      for (const m of ms) {
        if (m.startsWith("a:")) delete p.elements[m];
        else p.elements[m] = { ...(p.elements[m] || {}), hidden: true };
      }
      return { ...p, added: p.added.filter((a) => !addedIds.includes(a.id)) };
    });
    setPanel(null); setSelected(null);
  }, [update]);

  const groupSelected = () => {
    const g = `g${Date.now().toString(36)}`;
    update((p) => {
      const all = [...new Set(multi.flatMap((m) => groupPaths(p.elements, m)))];
      const z = Math.max(...all.map((k) => zOf(p.elements[k])));
      for (const m of all) p.elements[m] = { ...(p.elements[m] || {}), g, ...(z ? { z } : {}) };
      return p;
    });
    const first = multi[0];
    setMulti([]);
    setTimeout(() => post({ type: "at-ve-select", path: first }), 200);
    toast.success(`${multi.length} Elemente gruppiert`);
  };
  const duplicateItems = useCallback((items) => {
    if (!items?.length) return;
    const stamp = Date.now().toString(36);
    const ids = items.map((_, i) => `n${stamp}${i}`);
    update((p) => {
      const z = nextZ(p.elements, [], "front");
      const g = items.length > 1 ? `g${stamp}` : undefined;
      const copies = items.map((it, i) => {
        const orig = it.path.startsWith("a:") ? p.added.find((a) => a.id === it.path.slice(2)) : null;
        const { text, src, href, hidden, order, g: og, ...ov } = p.elements[it.path] || {};
        const style = orig ? {} : { color: it.color, fontSize: it.fontSize, fontWeight: it.fontWeight, width: it.width };
        p.elements[`a:${ids[i]}`] = clean({ ...style, ...ov, dx: (Number(ov.dx) || 0) + 20, dy: (Number(ov.dy) || 0) + 20, z, g });
        return orig ? { ...orig, id: ids[i], after: it.path } : { id: ids[i], after: it.path, kind: it.kind, text: it.text, src: it.src, href: it.href };
      });
      return { ...p, added: [...p.added, ...copies] };
    });
    setTimeout(() => { post({ type: "at-ve-select", path: `a:${ids[0]}` }); }, 250);
    toast.success(items.length > 1 ? `Gruppe kopiert (${items.length} Elemente)` : "Element kopiert");
  }, [update, post]);
  const ungroup = (path) => update((p) => {
    for (const m of groupPaths(p.elements, path)) {
      const { g, ...rest } = p.elements[m];
      if (Object.keys(rest).length) p.elements[m] = rest; else delete p.elements[m];
    }
    return p;
  });

  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin !== ORIGIN || !e.data) return;
      const d = e.data;
      if (d.type === "at-ve-ready") { post({ type: "at-ve-data", pages }); post({ type: "at-ve-mode", mode }); }
      if (d.type === "at-ve-selected") setSelected(d.info);
      if (d.type === "at-ve-multi") setMulti(d.paths);
      if (d.type === "at-ve-dup") duplicateItems(d.items);
      if (d.type === "at-ve-nudge") update((p) => {
        for (const k of d.paths) {
          const ov = p.elements[k] || {};
          p.elements[k] = clean({ ...ov, dx: (Number(ov.dx) || 0) + d.x || undefined, dy: (Number(ov.dy) || 0) + d.y || undefined });
        }
        return p;
      });
      if (d.type === "at-ve-open") setPanel(d.info);
      if (d.type === "at-ve-change") update((p) => {
        for (const [path, patch] of Object.entries(d.changes)) p.elements[path] = clean({ ...(p.elements[path] || {}), ...patch });
        return p;
      });
      if (d.type === "at-ve-delete") removeEl(d.path);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [pages, mode, post, update, removeEl, duplicateItems]);

  const switchTab = (path) => { setActive(path); setSelected(null); setPanel(null); };
  const closeTab = (path, e) => {
    e.stopPropagation();
    const rest = tabs.filter((t) => t !== path);
    if (!rest.length) return;
    setTabs(rest);
    if (active === path) switchTab(rest[0]);
  };
  const addElement = (kind) => {
    if (!selected) { toast.info("Bitte zuerst ein Element anklicken - das neue erscheint dahinter"); return; }
    const id = `n${Date.now().toString(36)}`;
    update((p) => ({ ...p, elements: { ...p.elements, [`a:${id}`]: { z: nextZ(p.elements, [`a:${id}`], "front") } }, added: [...p.added, { id, after: selected.path, kind, text: kind === "heading" ? "Neue Überschrift" : kind === "button" ? "Mehr erfahren" : kind === "tabs" ? DEFAULT_TABS : "Neuer Text", src: "", href: kind === "button" ? "/termin" : "" }] }));
    setTimeout(() => { post({ type: "at-ve-select", path: `a:${id}` }); setPanel({ path: `a:${id}` }); }, 250);
  };
  const undo = () => { if (!history.length) return; setPages(history[history.length - 1]); setHistory((h) => h.slice(0, -1)); };
  const request = async (url, method, ok, after) => {
    setBusy(true);
    try { await axios[method](`${API}${url}`, pages, auth); after(); toast.success(ok); }
    catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : "Speichern fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  if (!pages) return <div className="min-h-screen flex items-center justify-center text-[#888B8D]">Editor wird geladen ...</div>;
  const json = JSON.stringify(pages);
  const changedPage = (path) => JSON.stringify(pages[path] || {}) !== JSON.stringify(JSON.parse(published || "{}")[path] || {});
  const selGroup = selected ? groupPaths(page.elements, selected.path) : [];
  const panelGroup = panel ? groupPaths(page.elements, panel.path) : [];
  const panelAdded = panel?.path?.startsWith("a:") ? page.added.find((a) => a.id === panel.path.slice(2)) : null;

  return (
    <div className="h-screen flex flex-col bg-[#E9E9E7]" data-testid="visual-editor">
      <header className="h-12 bg-black text-white flex items-center gap-2 px-3 flex-shrink-0">
        <Link to="/admin/dashboard" className="flex items-center gap-1.5 text-sm text-[#D9D9D6] hover:text-white" data-testid="editor-back-link"><ArrowLeft className="w-4 h-4" /> Admin</Link>
        <div className="w-px h-6 bg-[#333] mx-1" />
        <span className="font-bold text-sm text-white">EDITOR</span>
        <div className="flex bg-[#1a1a1a] ml-3" data-testid="editor-mode-switch">
          {[["snap", Magnet, "Einrasten"], ["free", Move, "Frei"]].map(([m, Icon, l]) => (
            <button key={m} type="button" onClick={() => setMode(m)} className={`px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 ${mode === m ? "bg-[#EFDF00] text-black" : "text-[#888B8D] hover:text-white"}`} data-testid={`editor-mode-${m}`}>
              <Icon className="w-3.5 h-3.5" /> {l}
            </button>
          ))}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="ml-2 px-3 py-1.5 text-xs font-bold bg-[#1a1a1a] hover:bg-[#333] flex items-center gap-1.5" data-testid="editor-add-element-btn"><Plus className="w-3.5 h-3.5" /> Element</button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {ADD.map(([k, Icon, l]) => (
              <DropdownMenuItem key={k} onClick={() => addElement(k)} className="cursor-pointer" data-testid={`editor-add-${k}`}><Icon className="w-4 h-4 mr-2" />{l}</DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <button type="button" disabled={!selected} onClick={() => selected && removeEl(selected.path)} className="p-2 text-[#D9D9D6] hover:text-red-400 disabled:opacity-30" aria-label="Löschen" data-testid="editor-delete-selected-btn"><Trash2 className="w-4 h-4" /></button>
        {multi.length >= 2 && (
          <button type="button" onClick={groupSelected} className="px-3 py-1.5 text-xs font-bold bg-[#FF8A00] text-black hover:bg-[#ffa033] flex items-center gap-1.5 whitespace-nowrap" data-testid="editor-group-btn">
            <Group className="w-3.5 h-3.5" /> Gruppieren ({multi.length})
          </button>
        )}
        {!multi.length && selGroup.length > 1 && (
          <button type="button" onClick={() => ungroup(selected.path)} className="px-3 py-1.5 text-xs font-bold bg-[#1a1a1a] hover:bg-[#333] text-[#FF8A00] flex items-center gap-1.5 whitespace-nowrap" data-testid="editor-ungroup-btn">
            <Ungroup className="w-3.5 h-3.5" /> Gruppe lösen ({selGroup.length})
          </button>
        )}
        <button type="button" disabled={!selected} onClick={() => post({ type: "at-ve-dup-start" })} className="p-2 text-[#D9D9D6] hover:text-white disabled:opacity-30" aria-label="Kopieren" title="Kopieren (Strg+D)" data-testid="editor-duplicate-btn"><Copy className="w-4 h-4" /></button>
        <button type="button" onClick={undo} disabled={!history.length} className="p-2 text-[#D9D9D6] hover:text-white disabled:opacity-30" aria-label="Rückgängig" data-testid="editor-undo-btn"><Undo2 className="w-4 h-4" /></button>
        <span className="text-xs text-[#888B8D] ml-2 truncate hidden xl:block" data-testid="editor-selected-label">
          {multi.length ? `${multi.length} Elemente markiert · Umschalt+Klick = weitere hinzufügen` : selected ? `Ausgewählt: ${selected.label} · Doppelklick = Eigenschaften · Pfeiltasten = 1px (Umschalt 10px) · Strg+D = kopieren · Entf = löschen` : "Klicken = auswählen · Umschalt+Klick = mehrere · ziehen = verschieben · Doppelklick = Eigenschaften"}
        </span>
        <div className="flex-1" />
        <div className="flex bg-[#1a1a1a]">
          {DEVICES.map(([d, Icon]) => (
            <button key={d} type="button" onClick={() => setDevice(d)} aria-label={d} className={`p-2 ${device === d ? "bg-[#EFDF00] text-black" : "text-[#888B8D] hover:text-white"}`} data-testid={`editor-device-${d}`}><Icon className="w-4 h-4" /></button>
          ))}
        </div>
        <Link to="/admin/editor/bereiche" className="p-2 text-[#D9D9D6] hover:text-white" title="Startseiten-Bereiche" data-testid="editor-sections-link"><LayoutList className="w-4 h-4" /></Link>
        <a href={active} target="_blank" rel="noreferrer" className="p-2 text-[#D9D9D6] hover:text-white" aria-label="Live-Seite" data-testid="editor-open-live"><ExternalLink className="w-4 h-4" /></a>
        <Button onClick={() => request("/admin/page-overrides/draft", "put", "Entwurf gespeichert", () => setSaved(json))} disabled={busy || json === saved} variant="outline" className="h-8 bg-transparent border-[#555] text-white hover:bg-[#222] hover:text-white text-xs" data-testid="editor-save-draft-btn">
          <Save className="w-3.5 h-3.5 mr-1.5" /> Entwurf
        </Button>
        <Button onClick={() => request("/admin/page-overrides/publish", "post", "Veröffentlicht - jetzt live", () => { setSaved(json); setPublished(json); })} disabled={busy || json === published} className="h-8 bg-[#EFDF00] text-black hover:bg-[#D9C900] font-bold text-xs" data-testid="editor-publish-btn">
          <Rocket className="w-3.5 h-3.5 mr-1.5" /> Veröffentlichen
        </Button>
      </header>

      <nav className="h-10 bg-[#2a2a2a] flex items-end gap-0.5 px-2 overflow-x-auto flex-shrink-0" data-testid="editor-tabs">
        {tabs.map((path) => {
          const label = PAGES.find((p) => p[0] === path)?.[1] || path;
          const on = path === active;
          return (
            <div key={path} onClick={() => switchTab(path)} role="tab" aria-selected={on}
              className={`group h-8 min-w-[110px] max-w-[180px] px-3 flex items-center gap-2 text-xs cursor-pointer rounded-t-md transition-colors duration-150 ${on ? "bg-[#E9E9E7] text-black font-bold" : "bg-[#3a3a3a] text-[#ccc] hover:bg-[#474747]"}`}
              data-testid={`editor-tab-${path === "/" ? "home" : path.slice(1).replace(/\//g, "-")}`}>
              {changedPage(path) && <span className="w-1.5 h-1.5 rounded-full bg-[#EFDF00] flex-shrink-0" />}
              <span className="truncate flex-1">{label}</span>
              <button type="button" onClick={(e) => closeTab(path, e)} className="opacity-0 group-hover:opacity-100 hover:bg-black/10 rounded p-0.5" aria-label="Tab schließen" data-testid={`editor-tab-close-${path === "/" ? "home" : path.slice(1).replace(/\//g, "-")}`}><X className="w-3 h-3" /></button>
            </div>
          );
        })}
        {tabs.length < PAGES.length && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="h-8 w-8 flex items-center justify-center text-[#ccc] hover:bg-[#474747] rounded-t-md" aria-label="Tab öffnen" data-testid="editor-tab-add"><Plus className="w-4 h-4" /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {PAGES.filter((p) => !tabs.includes(p[0])).map(([path, l]) => (
                <DropdownMenuItem key={path} className="cursor-pointer" onClick={() => { setTabs([...tabs, path]); switchTab(path); }}>{l}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <button type="button" onClick={() => setPagesDialog(true)} className="ml-1 h-8 px-3 flex items-center gap-1.5 text-xs font-bold text-[#EFDF00] hover:bg-[#474747] rounded-t-md whitespace-nowrap" data-testid="editor-pages-btn">
          <FilePlus2 className="w-4 h-4" /> Neue Seite
        </button>
      </nav>

      <CustomPagesDialog open={pagesDialog} onOpenChange={setPagesDialog} pages={customPages} auth={auth}
        onChanged={(created, removed) => loadCustomPages().then(() => {
          if (created) { const path = `/seite/${created.slug}`; setTabs((t) => (t.includes(path) ? t : [...t, path])); switchTab(path); }
          if (removed) { const path = `/seite/${removed.slug}`; setTabs((t) => t.filter((x) => x !== path)); if (active === path) switchTab("/"); }
        })} />

      <div className="flex flex-1 min-h-0">
        <main className="flex-1 min-w-0 overflow-auto p-3 flex justify-center">
          <div className="bg-white shadow-xl h-full transition-[width] duration-300" style={{ width: DEVICES.find((d) => d[0] === device)[2] }}>
            <iframe key={active} ref={frame} src={`${active}?edit=1`} title="Vorschau" className="w-full h-full border-0" data-testid="editor-preview-frame" />
          </div>
        </main>
        {panel && (
          <ElementPanel key={panel.path} info={panel} ov={page.elements[panel.path] || {}} added={panelAdded}
            onChange={(patch) => patchEl(panel.path, patch)}
            layer={zOf(page.elements[panel.path])}
            groupSize={panelGroup.length}
            onUngroup={() => ungroup(panel.path)}
            onLayer={(act) => update((p) => {
              const ms = groupPaths(p.elements, panel.path);
              const z = nextZ(p.elements, ms, act);
              for (const m of ms) p.elements[m] = { ...(p.elements[m] || {}), z };
              return p;
            })}
            onAddedChange={(patch) => update((p) => ({ ...p, added: p.added.map((a) => (a.id === panelAdded.id ? { ...a, ...patch } : a)) }))}
            onDelete={() => removeEl(panel.path)}
            onReset={() => update((p) => { const g = p.elements[panel.path]?.g; if (g) p.elements[panel.path] = { g }; else delete p.elements[panel.path]; return p; })}
            onParent={() => post({ type: "at-ve-parent" })}
            onClose={() => setPanel(null)} />
        )}
      </div>
    </div>
  );
}
